import { plainToInstance, Type } from 'class-transformer';
import dayjs from 'dayjs';
import { Address } from './address';
import { Call } from './call';
import { Partner } from './partner';
import { Email } from './email';
import { User } from './user';
import { DisputeItem } from './dispute_item';

import { BaseType } from './model';
import type { PhoneContact } from './phone_contact';
import { TransformStringToDayJS } from './utils';
import { ChildUnifiedModelMixin, ModelSyncStatus, UnifiedModelMixin } from './unified_model';
import { defaultTimezone } from '@ui/components/time/tz';
import { Payment } from './payment';
import { PaymentAllocation } from './payment_allocation';
import { PaymentPromise } from './payment_promise';
import { OutreachChannel } from './types';
import { TaskStatus } from './task';

export enum InvoiceStatus {
  OVERDUE = 'overdue',
  OPEN = 'open',
  DRAFT = 'draft',
  PROCESSING = 'processing',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  PAYMENT_PROMISED = 'promised',
  BROKEN_PAYMENT_PROMISE = 'broken_promise',
  UNCOLLECTIBLE = 'uncollectible',
  VOID = 'void',
  PENDING_SYNC = 'pending_sync',
  UNKNOWN = 'unknown',
}

export const InvoiceStatusLabels = {
  [InvoiceStatus.OVERDUE]: 'Overdue',
  [InvoiceStatus.OPEN]: 'Open',
  [InvoiceStatus.DRAFT]: 'Draft',
  [InvoiceStatus.PROCESSING]: 'Processing',
  [InvoiceStatus.PARTIALLY_PAID]: 'Partially Paid',
  [InvoiceStatus.PAYMENT_PROMISED]: 'Promised',
  [InvoiceStatus.BROKEN_PAYMENT_PROMISE]: 'Broken Promise',
  [InvoiceStatus.PAID]: 'Paid',
  [InvoiceStatus.UNCOLLECTIBLE]: 'Uncollectible',
  [InvoiceStatus.VOID]: 'Void',
  [InvoiceStatus.PENDING_SYNC]: 'Pending Sync',
  [InvoiceStatus.UNKNOWN]: 'Unknown',
};

export interface InvoiceStatusDetail {
  displayName: string;
  class: string | string[];
  amountClass: string | string[];
}

export const TerminalStatuses = [
  InvoiceStatus.UNCOLLECTIBLE,
  InvoiceStatus.VOID,
  InvoiceStatus.PAID,
];

export enum InvoiceType {
  PAYABLE = 'payable',

  RECEIVABLE = 'receivable',
}

export enum ReconciliationStatus {
  SENT = 'sent',
  COLLECTION = 'collection',
  MATCHED = 'matched',
  RECONCILED = 'reconciled',
}

export const RankedStatuses: Record<ReconciliationStatus, number> = {
  [ReconciliationStatus.SENT]: 0,
  [ReconciliationStatus.COLLECTION]: 1,
  [ReconciliationStatus.MATCHED]: 2,
  [ReconciliationStatus.RECONCILED]: 3,
};

export class InvoiceLineItem extends ChildUnifiedModelMixin {
  id: string;

  currency: string;

  description: string;

  invoice_id: string;

  @TransformStringToDayJS()
  period_end_date: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  period_start_date: dayjs.Dayjs | null;

  product_type: string | null;

  quantity: number;

  remote_id: string;

  total_amount: number;

  total_discount: number | null;

  total_tax: number | null;

  unit_amount: number | null;

  custom_fields: Record<string, { label: string; value: string }> | null;

  get price(): number {
    return this.total_amount / this.quantity;
  }
}

export class InvoicePayment extends PaymentAllocation {
  @Type(() => Payment)
  payment: Payment;
}

export class Invoice extends UnifiedModelMixin {
  static fromJson<T extends BaseType>(this: new () => T, json: any): T {
    const invoice: Invoice = plainToInstance(this, json, {
      exposeDefaultValues: true,
      exposeUnsetFields: true,
    }) as unknown as Invoice;

    if (invoice.calls) invoice.calls.forEach((call) => (call.partner = invoice.partner));

    return invoice as unknown as T;
  }

  id: string;

  amount_due: number;

  amount_paid: number;

  amount_remaining: number;

  @Type(() => User)
  assignee: User | null;

  @Type(() => Address)
  billing_address: Address | null;

  billing_email: string | null;

  @Type(() => Call)
  calls: Call[] | null;

  @Type(() => Email)
  emails: Email[] | null;

  currency: string;

  custom_fields: Record<string, { label: string; value: string }> | null;

  @TransformStringToDayJS()
  due_date: dayjs.Dayjs | null;

  in_dispute: boolean;

  dispute_item_id: string;

  @Type(() => DisputeItem)
  dispute_items: DisputeItem[];

  is_assigned_to_agent: boolean;

  @TransformStringToDayJS()
  issued_at: dayjs.Dayjs | null;

  last_outreach_channel: OutreachChannel | null;

  @TransformStringToDayJS()
  last_outreach_at: dayjs.Dayjs | null;

  last_outreach_to: string | null;

  @Type(() => InvoiceLineItem)
  line_items?: InvoiceLineItem[];

  @TransformStringToDayJS()
  marked_uncollectible_at: dayjs.Dayjs | null;

  memo: string | null;

  number: string | null;

  private_note: string | null;

  order_number: string | null;

  @TransformStringToDayJS()
  paid_at: dayjs.Dayjs | null;

  @Type(() => Partner)
  partner: Partner;

  payment_url: string | null;

  @Type(() => InvoicePayment)
  payments?: InvoicePayment[];

  pdf: string | null;

  pdf_id: string | null;

  @TransformStringToDayJS()
  period_end: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  period_start: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  promised_to_pay_date: dayjs.Dayjs | null;

  current_payment_promise_id: string | null;

  @Type(() => PaymentPromise)
  current_payment_promise: PaymentPromise | null;

  purchase_order_number: string | null;

  remote_id: string;

  remote_invoice_pdf: string | null;

  remote_payment_portal_url: string | null;

  remote_status: string;

  @Type(() => Address)
  shipping_address: Address | null;

  status: InvoiceStatus;

  subtotal: number;

  timezone: string;

  total_amount: number;

  total_discount: number | null;

  total_tax: number;

  type: InvoiceType;

  partner_id: string;

  pending_amount_remaining: number;

  @TransformStringToDayJS()
  voided_at: dayjs.Dayjs | null;

  get availablePhoneContacts(): PhoneContact[] {
    const contacts: Record<string, PhoneContact> = {};

    if (this.partner.phone)
      contacts[this.partner.phone] = {
        name: this.partner.name,
        phone_number: this.partner.phone,
      };

    if (this.billing_address?.phone && !(this.billing_address.phone in contacts))
      contacts[this.billing_address.phone] = {
        name: this.partner.name,
        phone_number: this.billing_address.phone,
      };

    if (this.shipping_address?.phone && !(this.shipping_address.phone in contacts))
      contacts[this.shipping_address.phone] = {
        name: this.partner.name,
        phone_number: this.shipping_address.phone,
      };

    return Object.values(contacts);
  }

  // TODO: read reconciliation status directly from invoice when set in backend
  get reconciliation_status(): string {
    switch (this.status) {
      case InvoiceStatus.DRAFT:
      case InvoiceStatus.OPEN:
        return ReconciliationStatus.SENT;
      case InvoiceStatus.PAID:
      case InvoiceStatus.VOID:
      case InvoiceStatus.UNCOLLECTIBLE:
        return ReconciliationStatus.RECONCILED;
      default:
        return ReconciliationStatus.COLLECTION;
    }
  }

  get isOverdue(): boolean {
    if (!this.isOpen || !this.due_date) return false;
    const dueDate = this.dueDateStartOfDay;
    const now = this.nowStartOfDay;
    if (!dueDate || !now) return false;
    // Invoice becomes overdue the day AFTER the due date
    // e.g., due date 1/16: overdue starting 1/17
    return dueDate.isBefore(now, 'day');
  }

  get isOpen(): boolean {
    return this.status === InvoiceStatus.OPEN;
  }

  get isTerminal(): boolean {
    return TerminalStatuses.includes(this.status);
  }

  get nowStartOfDay(): dayjs.Dayjs {
    return dayjs.utc()?.tz(defaultTimezone)?.startOf('day');
  }

  get dueDateStartOfDay(): dayjs.Dayjs | null {
    if (!this.due_date) return null;

    if (this.due_date.hour() === 0 && this.due_date.minute() === 0) {
      return this.due_date;
    }

    return this.due_date?.clone()?.tz(defaultTimezone)?.startOf('day');
  }

  get daysOverdue(): number | null {
    if (!this.isOverdue || !this.due_date) return null;

    return this.nowStartOfDay?.diff(this.dueDateStartOfDay, 'days');
  }

  get daysUntilDue(): number | null {
    const dueDate = this.dueDateStartOfDay;

    if (!this.isOpen || !dueDate) return null;

    return dueDate?.diff(this.nowStartOfDay, 'days');
  }

  get recipientEmail(): string | null {
    return this.partner.email;
  }

  get download_url(): string | null {
    return this.pdf || this.remote_invoice_pdf;
  }

  get hasNonTerminalDispute() {
    return this.in_dispute || this.isDisputeProposed;
  }

  get openDisputeItem() {
    return this.dispute_items?.find((item) => !item.isTerminal);
  }

  get openDisputeId(): string | undefined {
    return this.openDisputeItem?.dispute_id;
  }

  get latestDisputeItem() {
    return (
      this.openDisputeItem ??
      this.dispute_items
        ?.filter((item) => item.isTerminal)
        .sort((a, b) => b.created_at.unix() - a.created_at.unix())[0]
    );
  }

  get latestDisputeId(): string | undefined {
    return this.latestDisputeItem?.dispute_id;
  }

  get isDisputeProposed() {
    return this.dispute_items?.find((item) => item.isProposed);
  }

  get isPaid() {
    return this.status === InvoiceStatus.PAID;
  }

  get isPromise() {
    return this.isOpen && this.promised_to_pay_date;
  }

  get isBrokenPromise() {
    const today = dayjs.utc();

    return (
      !this.isTerminal &&
      this.promised_to_pay_date?.isBefore(today) &&
      this.due_date?.isBefore(today)
    );
  }

  get displayStatus(): InvoiceStatus {
    if (this.isBrokenPromise) return InvoiceStatus.BROKEN_PAYMENT_PROMISE;
    if (this.isPromise) return InvoiceStatus.PAYMENT_PROMISED;
    return this.status;
  }
}
